import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import StrategyConfigModal from './StrategyConfigModal';
import StrategyLogsModal from './StrategyLogsModal';
import { useFeatureFlags } from '../../config/featureFlags';

interface LiveCoinData {
  coin: string;
  symbol: string;
  currentPrice: number;
  marketType: string;
  buyTargetPrice: number;
  positions: Array<{
    strategyId: number;
    strategyName: string;
    positionType: 'LONG' | 'SHORT';
    entryPrice: number;
    currentPnl: number;
    positionId?: string | null;
    closeOrderId?: string | null;
    entryDate?: string;
    status?: string;
    takeProfitPrice?: number;
    takeProfitPercent?: number;
    trailingStopLossPrice?: number;
    trailingStopLossPercent?: number;
    // US6 / US16: when using hedge live data, each position row may represent a hedge leg
    // and expose its underlying hedgePositionId so we can perform leg-specific actions (close).
    hedgePositionId?: number;
  }>;
}

interface LiveDataPanelProps {
  marketType?: string;
}

interface Alert {
  coin: string;
  type: 'BUY' | 'SELL';
  message: string;
  timestamp: Date;
  strategyName?: string;
  strategyId?: number;
  targetPrice?: number;
  currentPrice?: number;
  targetType?: 'TP' | 'TSL';
  // Store position data for calculations
  entryPrice?: number;
  trailingStopLossPrice?: number;
  takeProfitPrice?: number;
  positionType?: 'LONG' | 'SHORT';
  currentPnl?: number;
  positionId?: string | null; // For unique identification
  // For basket TP calculation
  allPositionsForStrategy?: Array<{entryPrice: number, currentPrice: number, positionType: 'LONG' | 'SHORT', currentPnl: number}>;
  basketTakeProfitConfig?: number;
  // For market activity
  marketActivity24h?: number;
}

const LiveDataPanel: React.FC<LiveDataPanelProps> = ({ marketType: propMarketType = 'FUTURES' }) => {
  const adminApiService = useAdminApiService();
  const { flags } = useFeatureFlags();
  const [liveData, setLiveData] = useState<LiveCoinData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketType, setMarketType] = useState<string>(propMarketType);
  const [refreshInterval, setRefreshInterval] = useState<number>(5000);
  const [useHedgeView, setUseHedgeView] = useState<boolean>(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showAllPositions, setShowAllPositions] = useState(false);
  const [sortColumn, setSortColumn] = useState<'date' | 'type' | 'status' | 'strategy' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [alertSortColumn, setAlertSortColumn] = useState<'coin' | 'alertType' | 'type' | 'strategy' | null>(null);
  const [alertSortDirection, setAlertSortDirection] = useState<'asc' | 'desc'>('asc');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [selectedStrategyName, setSelectedStrategyName] = useState<string>('');
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [strategyConfigs, setStrategyConfigs] = useState<Map<number, any>>(new Map());
  const [tooltipContent, setTooltipContent] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedTriggerAlert, setSelectedTriggerAlert] = useState<Alert | null>(null);
  const [hedgePositions, setHedgePositions] = useState<any[] | null>(null);
  const [hedgeLoading, setHedgeLoading] = useState(false);
  const [hedgeError, setHedgeError] = useState<string | null>(null);
  const [hedgeStrategyName, setHedgeStrategyName] = useState<string>('');
  // US15: Filter state for positions and alerts
  const [filterPositionType, setFilterPositionType] = useState<'ALL' | 'LONG' | 'SHORT'>('ALL');
  const [filterSymbol, setFilterSymbol] = useState<string>('');
  const [filterStrategy, setFilterStrategy] = useState<string>('');
  const [filterAlertType, setFilterAlertType] = useState<'ALL' | 'LONG' | 'SHORT'>('ALL');
  // US10: Combined pair view toggle
  const [showCombinedPairView, setShowCombinedPairView] = useState<boolean>(false);
  // US11: Personal trading accounts for account display
  const [personalAccounts, setPersonalAccounts] = useState<{ id: number; name: string }[]>([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  
  // Logs functionality state
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsStrategyName, setLogsStrategyName] = useState<string>('');
  const [expandedLogsStrategy, setExpandedLogsStrategy] = useState<string | null>(null);
  const [expandedLogsRowKey, setExpandedLogsRowKey] = useState<string | null>(null); // Track which specific row was clicked
  // US13: Store filters for the expanded logs (symbol, side, positionId)
  const [expandedLogsFilters, setExpandedLogsFilters] = useState<{ symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string } | null>(null);
  const [inlineLogs, setInlineLogs] = useState<Map<string, { logs: string[], loading: boolean, error: string | null, autoRefresh: boolean }>>(new Map());
  const inlineLogsIntervalRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handle tooltip click/tap for mobile
  const handleTooltipClick = (e: React.MouseEvent<HTMLElement>, content: string) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top - 10 });
      setTooltipContent(content);
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setTooltipContent(null);
        setTooltipPosition(null);
      }, 3000);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    if (tooltipContent && isMobile) {
      const handleClickOutside = () => {
        setTooltipContent(null);
        setTooltipPosition(null);
      };
      // Small delay to avoid immediate close
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [tooltipContent, isMobile]);

  const handleHoverStrategy = (strategyId: number, strategyName: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Set timeout to open modal after 500ms hover
    hoverTimeoutRef.current = setTimeout(() => {
      setSelectedStrategyId(strategyId);
      setSelectedStrategyName(strategyName);
      setConfigModalOpen(true);
    }, 500);
  };

  const handleLeaveStrategy = () => {
    // Clear timeout if user moves away before 500ms
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // US13: Fetch inline logs for a strategy, with optional leg-specific filters
  const fetchInlineLogs = async (
    strategyName: string, 
    lineCount: number = 200,
    filters?: { symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string }
  ) => {
    if (!strategyName) return;
    
    // Create a unique key that includes filters for caching
    const logKey = filters 
      ? `${strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
      : strategyName;
    
    setInlineLogs(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(logKey)) {
        newMap.set(logKey, { logs: [], loading: true, error: null, autoRefresh: true });
      } else {
        const current = newMap.get(logKey)!;
        newMap.set(logKey, { ...current, loading: true, error: null });
      }
      return newMap;
    });
    
    try {
      // US13: Pass filters to getStrategyLogs
      const response = await adminApiService.getStrategyLogs(strategyName, lineCount, filters || undefined);
      if (response.success && response.data && response.data.logs) {
        setInlineLogs(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(logKey) || { logs: [], loading: false, error: null, autoRefresh: true };
          // Always update with fresh logs
          newMap.set(logKey, { 
            ...current, 
            logs: response.data!.logs || [], 
            loading: false, 
            error: null,
            // Preserve autoRefresh setting
            autoRefresh: current.autoRefresh !== undefined ? current.autoRefresh : true
          });
          return newMap;
        });
      } else {
        setInlineLogs(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(logKey) || { logs: [], loading: false, error: null, autoRefresh: true };
          newMap.set(logKey, { ...current, loading: false, error: response.error || 'Failed to fetch logs' });
          return newMap;
        });
      }
    } catch (err: any) {
      setInlineLogs(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(logKey) || { logs: [], loading: false, error: null, autoRefresh: true };
        newMap.set(logKey, { ...current, loading: false, error: err.message || 'Failed to fetch logs' });
        return newMap;
      });
    }
  };

  // US13: Toggle expanded logs for a strategy, with optional leg-specific filters
  const toggleExpandedLogs = (
    strategyName: string, 
    rowKey?: string,
    filters?: { symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string }
  ) => {
    const currentRowKey = rowKey || `${strategyName}-default`;
    if (expandedLogsStrategy === strategyName && expandedLogsRowKey === currentRowKey) {
      // Collapse - stop auto-refresh
      setExpandedLogsStrategy(null);
      setExpandedLogsRowKey(null);
      setExpandedLogsFilters(null);
    } else {
      // Expand - fetch logs immediately with filters
      setExpandedLogsStrategy(strategyName);
      setExpandedLogsRowKey(currentRowKey);
      setExpandedLogsFilters(filters || null);
      // Fetch will be triggered by useEffect
    }
  };

  // Open logs modal
  const openLogsModal = (strategyName: string) => {
    setLogsStrategyName(strategyName);
    setLogsModalOpen(true);
  };

  // US13: Fetch logs when strategy is expanded and manage auto-refresh (with leg-specific filters)
  useEffect(() => {
    if (!expandedLogsStrategy) {
      // Clean up any existing intervals when collapsed
      inlineLogsIntervalRef.current.forEach(interval => clearInterval(interval));
      inlineLogsIntervalRef.current.clear();
      return;
    }

    // US13: Fetch logs immediately when expanded, with filters if available
    fetchInlineLogs(expandedLogsStrategy, 200, expandedLogsFilters || undefined);

    // Set up auto-refresh interval - always fetch fresh data with filters
    const interval = setInterval(() => {
      fetchInlineLogs(expandedLogsStrategy, 200, expandedLogsFilters || undefined);
    }, 5000);

    const logKey = expandedLogsFilters 
      ? `${expandedLogsStrategy}-${expandedLogsFilters.symbol || ''}-${expandedLogsFilters.side || ''}-${expandedLogsFilters.positionId || ''}`
      : expandedLogsStrategy;
    inlineLogsIntervalRef.current.set(logKey, interval);

    // Cleanup function
    return () => {
      const existingInterval = inlineLogsIntervalRef.current.get(logKey);
      if (existingInterval) {
        clearInterval(existingInterval);
        inlineLogsIntervalRef.current.delete(logKey);
      }
    };
  }, [expandedLogsStrategy, expandedLogsFilters]);

  // Handle auto-refresh toggle - restart interval when autoRefresh changes
  // We use a ref to track the last known autoRefresh state to avoid recreating interval on every log update
  const autoRefreshStateRef = useRef<Map<string, boolean>>(new Map());
  
  useEffect(() => {
    if (!expandedLogsStrategy) return;

    const logsData = inlineLogs.get(expandedLogsStrategy);
    const currentInterval = inlineLogsIntervalRef.current.get(expandedLogsStrategy);
    const lastAutoRefresh = autoRefreshStateRef.current.get(expandedLogsStrategy);
    const currentAutoRefresh = logsData?.autoRefresh ?? true;

    // Only recreate interval if autoRefresh state actually changed
    if (lastAutoRefresh !== currentAutoRefresh) {
      autoRefreshStateRef.current.set(expandedLogsStrategy, currentAutoRefresh);

      if (currentAutoRefresh) {
        // Auto-refresh is enabled - ensure interval is running
        if (!currentInterval) {
          const logKey = expandedLogsFilters 
            ? `${expandedLogsStrategy}-${expandedLogsFilters.symbol || ''}-${expandedLogsFilters.side || ''}-${expandedLogsFilters.positionId || ''}`
            : expandedLogsStrategy;
          const interval = setInterval(() => {
            fetchInlineLogs(expandedLogsStrategy, 200, expandedLogsFilters || undefined);
          }, 5000);
          inlineLogsIntervalRef.current.set(logKey, interval);
        }
      } else {
        // Auto-refresh is disabled - stop interval
        if (currentInterval) {
          clearInterval(currentInterval);
          inlineLogsIntervalRef.current.delete(expandedLogsStrategy);
        }
      }
    }
  }, [expandedLogsStrategy, inlineLogs]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      inlineLogsIntervalRef.current.forEach(interval => clearInterval(interval));
      inlineLogsIntervalRef.current.clear();
    };
  }, []);

  // Flatten positions into rows
  const tableRows = useMemo(() => {
    const rows: Array<{coin: string, symbol: string, currentPrice: number, position: LiveCoinData['positions'][0]}> = [];
    const seenPositionIds = new Set<string>();
    const seenPositions = new Map<string, boolean>(); // Track by coin+strategyId+entryPrice+positionType
    
    liveData.forEach(coinData => {
      // Filter to only show ACTIVE/OPEN positions (exclude CLOSED, CANCELLED, etc.)
      const activePositions = coinData.positions.filter(position => {
        const status = position.status?.toUpperCase() || '';
        // Accept ACTIVE, OPEN, or empty/null (assume active if not specified)
        return !status || status === 'ACTIVE' || status === 'OPEN';
      });
      
      activePositions.forEach(position => {
        // Create a unique key for deduplication
        // Use positionId if available, otherwise use combination of identifiers
        let positionKey: string;
        
        if (position.positionId) {
          positionKey = position.positionId;
        } else {
          // For positions without positionId, use a combination that should be unique
          // Include positionType, entryPrice, and entryDate to handle same coin/strategy but different positions
          const entryDateStr = position.entryDate || '';
          positionKey = `${coinData.coin}-${position.strategyId}-${position.positionType}-${position.entryPrice}-${entryDateStr}`;
        }
        
        // Additional check: if no positionId, also check if we've seen this exact combination
        // This helps catch duplicates that might have slightly different entry prices due to rounding
        if (!position.positionId) {
          const entryDateStr = position.entryDate || '';
          const roundedPrice = Math.round(position.entryPrice * 1000000);
          const comboKey = `${coinData.coin}-${position.strategyId}-${position.positionType}-${roundedPrice}-${entryDateStr}`;
          if (seenPositions.has(comboKey)) {
            return; // Skip duplicate
          }
          seenPositions.set(comboKey, true);
        }
        
        if (!seenPositionIds.has(positionKey)) {
          seenPositionIds.add(positionKey);
          rows.push({
            coin: coinData.coin,
            symbol: coinData.symbol,
            currentPrice: coinData.currentPrice,
            position
          });
        }
      });
    });
    
    // Apply sorting if a column is selected
    if (sortColumn && rows.length > 0) {
      return [...rows].sort((a, b) => {
        // Handle rows without positions
        if (!a.position || !b.position) {
          if (!a.position && !b.position) return 0;
          return !a.position ? 1 : -1;
        }
        
        let aValue: string | number;
        let bValue: string | number;
        
        switch (sortColumn) {
          case 'date':
            // Sort by entryDate (timestamp)
            aValue = a.position.entryDate ? new Date(a.position.entryDate).getTime() : 0;
            bValue = b.position.entryDate ? new Date(b.position.entryDate).getTime() : 0;
            break;
          case 'type':
            aValue = a.position.positionType || '';
            bValue = b.position.positionType || '';
            break;
          case 'status':
            aValue = a.position.status || 'ACTIVE';
            bValue = b.position.status || 'ACTIVE';
            break;
          case 'strategy':
            aValue = a.position.strategyName || '';
            bValue = b.position.strategyName || '';
            break;
          default:
            return 0;
        }
        
        let comparison: number;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    
    // US15: Apply filters before returning
    let filtered = rows;
    if (filterPositionType !== 'ALL') {
      filtered = filtered.filter(row => row.position?.positionType === filterPositionType);
    }
    if (filterSymbol) {
      filtered = filtered.filter(row => row.coin.toLowerCase().includes(filterSymbol.toLowerCase()));
    }
    if (filterStrategy) {
      filtered = filtered.filter(row => row.position?.strategyName?.toLowerCase().includes(filterStrategy.toLowerCase()));
    }
    return filtered;
  }, [liveData, sortColumn, sortDirection, filterPositionType, filterSymbol, filterStrategy]);

  // US10: Create combined pair view - aggregate LONG and SHORT legs on the same symbol
  const combinedPairRows = useMemo(() => {
    if (!showCombinedPairView || !useHedgeView) {
      return null; // Only show combined view when both toggles are enabled
    }

    // Group positions by symbol + strategy
    const pairMap = new Map<string, {
      coin: string;
      symbol: string;
      currentPrice: number;
      longLeg: typeof tableRows[0] | null;
      shortLeg: typeof tableRows[0] | null;
      strategyId: number;
      strategyName: string;
    }>();

    tableRows.forEach(row => {
      if (!row.position) return;
      
      const key = `${row.coin}-${row.position.strategyId}`;
      const existing = pairMap.get(key);
      
      if (!existing) {
        pairMap.set(key, {
          coin: row.coin,
          symbol: row.symbol,
          currentPrice: row.currentPrice,
          longLeg: null,
          shortLeg: null,
          strategyId: row.position.strategyId,
          strategyName: row.position.strategyName || 'Unknown'
        });
      }
      
      const pair = pairMap.get(key)!;
      if (row.position.positionType === 'LONG') {
        pair.longLeg = row;
      } else if (row.position.positionType === 'SHORT') {
        pair.shortLeg = row;
      }
    });

    // Convert to array and filter to only pairs that have both legs
    const pairs = Array.from(pairMap.values())
      .filter(pair => pair.longLeg && pair.shortLeg)
      .map(pair => {
        const longPnl = pair.longLeg!.position.currentPnl || 0;
        const shortPnl = pair.shortLeg!.position.currentPnl || 0;
        const netPnl = longPnl - shortPnl; // Net hedged PnL
        
        // Calculate exposure (entry price * size approximation)
        const longExposure = Math.abs(longPnl) + (pair.longLeg!.position.entryPrice * 30) || 0; // Approximate
        const shortExposure = Math.abs(shortPnl) + (pair.shortLeg!.position.entryPrice * 30) || 0; // Approximate
        const combinedExposure = longExposure + shortExposure;

        return {
          coin: pair.coin,
          symbol: pair.symbol,
          currentPrice: pair.currentPrice,
          strategyId: pair.strategyId,
          strategyName: pair.strategyName,
          longLeg: pair.longLeg!,
          shortLeg: pair.shortLeg!,
          netPnl,
          combinedExposure,
          longPnl,
          shortPnl,
          longEntryPrice: pair.longLeg!.position.entryPrice,
          shortEntryPrice: pair.shortLeg!.position.entryPrice,
          longPositionId: pair.longLeg!.position.positionId,
          shortPositionId: pair.shortLeg!.position.positionId
        };
      });

    return pairs;
  }, [tableRows, showCombinedPairView, useHedgeView]);

  // Handle column header click for sorting
  const handleSort = (column: 'date' | 'type' | 'status' | 'strategy') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle alert column header click for sorting
  const handleAlertSort = (column: 'coin' | 'alertType' | 'type' | 'strategy') => {
    if (alertSortColumn === column) {
      // Toggle direction if clicking the same column
      setAlertSortDirection(alertSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setAlertSortColumn(column);
      setAlertSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: 'date' | 'type' | 'status' | 'strategy') => {
    if (sortColumn !== column) {
      return <span style={{ marginLeft: '4px', color: '#9ca3af', fontSize: '10px' }}>↕</span>;
    }
    return (
      <span style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px', fontWeight: 600 }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Render alert sort indicator
  const renderAlertSortIndicator = (column: 'coin' | 'alertType' | 'type' | 'strategy') => {
    if (alertSortColumn !== column) {
      return <span style={{ marginLeft: '4px', color: '#9ca3af', fontSize: '10px' }}>↕</span>;
    }
    return (
      <span style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px', fontWeight: 600 }}>
        {alertSortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Load strategy configs to get basket TP and market activity threshold
  const loadStrategyConfigs = async () => {
    try {
      const strategiesResponse = await adminApiService.getAllTradingStrategies();
      if (strategiesResponse.success && strategiesResponse.data) {
        const configMap = new Map<number, any>();
        strategiesResponse.data.forEach((strategy: any) => {
          try {
            const config = typeof strategy.config === 'string' 
              ? JSON.parse(strategy.config) 
              : strategy.config;
            configMap.set(strategy.id, config);
          } catch (e) {
            console.error(`Failed to parse config for strategy ${strategy.id}:`, e);
          }
        });
        setStrategyConfigs(configMap);
      }
    } catch (err) {
      console.error('Error loading strategy configs:', err);
    }
  };

  // US11: Load personal trading accounts for account display
  const loadPersonalAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await adminApiService.getMyTradingAccounts();
      if (response.success && response.data) {
        const list = (response.data as any[]).map(acc => ({
          id: acc.id,
          name: acc.name as string,
        }));
        setPersonalAccounts(list);
      }
    } catch (err) {
      console.error('Error loading personal accounts:', err);
    } finally {
      setAccountsLoading(false);
    }
  };

  // US11: Helper to get account name from kucoinConfigId
  const getAccountName = (strategyId: number): string => {
    const config = strategyConfigs.get(strategyId);
    if (!config) {
      return 'Shared KuCoin (System)';
    }
    const kucoinConfigId = (config as any).kucoinConfigId as number | undefined;
    if (!kucoinConfigId) {
      return 'Shared KuCoin (System)';
    }
    const match = personalAccounts.find(a => a.id === kucoinConfigId);
    return match ? match.name : `Account #${kucoinConfigId}`;
  };

  const loadLiveData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = useHedgeView
        ? await adminApiService.getHedgeLiveData(marketType)
        : await adminApiService.getLiveData(marketType);
      
      if (response.success && response.data) {
        setLiveData(response.data);
        setLastRefresh(new Date());
        checkAlerts(response.data);
      } else {
        setError(response.error || 'Failed to load live data');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading live data');
      console.error('Error loading live data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    loadStrategyConfigs();
    loadPersonalAccounts(); // US11: Load personal accounts
    loadLiveData();
    
    const interval = setInterval(() => {
      loadLiveData();
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [marketType, refreshInterval, useHedgeView]);

  // Helper function to create a unique key for an alert
  const getAlertKey = (coin: string, strategyId: number | undefined, positionType: 'LONG' | 'SHORT' | undefined, targetType: 'TP' | 'TSL' | undefined, entryPrice: number | undefined, positionId?: string | null): string => {
    // Use positionId if available, otherwise use entryPrice as fallback
    const positionIdentifier = positionId || (entryPrice ? entryPrice.toString() : 'unknown');
    return `${coin}-${strategyId}-${positionType}-${targetType}-${positionIdentifier}`;
  };

  const checkAlerts = (data: LiveCoinData[]) => {
    const alertKeyMap = new Map<string, Alert>(); // Track alerts by unique key
    
    // Build current alerts from live data
    data.forEach(coinData => {
      coinData.positions.forEach(position => {
        // Check Take Profit
        // US12: Alerts are leg-specific - each alert is tied to a specific leg (LONG/SHORT) with its positionId
        if (position.takeProfitPrice && coinData.currentPrice) {
          const tpDiff = Math.abs(coinData.currentPrice - position.takeProfitPrice) / position.takeProfitPrice * 100;
          if (tpDiff <= 1.0) {
            const config = strategyConfigs.get(position.strategyId);
            // US12: Enhanced message to clearly identify which leg (LONG/SHORT) triggered the alert
            const legIdentifier = position.positionType ? `[${position.positionType}]` : '';
            const positionIdSuffix = position.positionId ? ` (ID: ${position.positionId.substring(0, 8)}...)` : '';
            const alert: Alert = {
              coin: coinData.coin,
              type: 'SELL',
              message: `${coinData.coin} ${legIdentifier} (${position.strategyName})${positionIdSuffix} - ${position.positionType || 'Position'} leg near Take Profit`,
              timestamp: new Date(), // Will be updated if alert exists
              strategyName: position.strategyName,
              strategyId: position.strategyId,
              targetPrice: position.takeProfitPrice,
              currentPrice: coinData.currentPrice,
              targetType: 'TP',
              entryPrice: position.entryPrice,
              trailingStopLossPrice: position.trailingStopLossPrice,
              takeProfitPrice: position.takeProfitPrice,
              positionType: position.positionType,
              currentPnl: position.currentPnl,
              positionId: position.positionId, // US12: Unique identifier for this specific leg
              basketTakeProfitConfig: config?.basketTakeProfit
            };
            // US12: Alert key includes positionId to ensure uniqueness per leg
            const key = getAlertKey(
              coinData.coin,
              position.strategyId,
              position.positionType,
              'TP',
              position.entryPrice,
              position.positionId
            );
            alertKeyMap.set(key, alert);
          }
        }
        
        // Check Trailing Stop Loss
        // US12: Alerts are leg-specific - each alert is tied to a specific leg (LONG/SHORT) with its positionId
        if (position.trailingStopLossPrice && coinData.currentPrice) {
          const tslDiff = Math.abs(coinData.currentPrice - position.trailingStopLossPrice) / position.trailingStopLossPrice * 100;
          if (tslDiff <= 1.0) {
            const config = strategyConfigs.get(position.strategyId);
            // US12: Enhanced message to clearly identify which leg (LONG/SHORT) triggered the alert
            const legIdentifier = position.positionType ? `[${position.positionType}]` : '';
            const positionIdSuffix = position.positionId ? ` (ID: ${position.positionId.substring(0, 8)}...)` : '';
            const alert: Alert = {
              coin: coinData.coin,
              type: 'SELL',
              message: `${coinData.coin} ${legIdentifier} (${position.strategyName})${positionIdSuffix} - ${position.positionType || 'Position'} leg near Trailing Stop Loss`,
              timestamp: new Date(), // Will be updated if alert exists
              strategyName: position.strategyName,
              strategyId: position.strategyId,
              targetPrice: position.trailingStopLossPrice,
              currentPrice: coinData.currentPrice,
              targetType: 'TSL',
              entryPrice: position.entryPrice,
              trailingStopLossPrice: position.trailingStopLossPrice,
              takeProfitPrice: position.takeProfitPrice,
              positionType: position.positionType,
              currentPnl: position.currentPnl,
              positionId: position.positionId, // US12: Unique identifier for this specific leg
              basketTakeProfitConfig: config?.basketTakeProfit
            };
            // US12: Alert key includes positionId to ensure uniqueness per leg
            const key = getAlertKey(
              coinData.coin,
              position.strategyId,
              position.positionType,
              'TSL',
              position.entryPrice,
              position.positionId
            );
            alertKeyMap.set(key, alert);
          }
        }
      });
    });
    
    // Update alerts: preserve timestamps for existing alerts, update data
    setAlerts(prev => {
      const updatedAlerts: Alert[] = [];
      const prevKeyMap = new Map<string, Alert>();
      
      // Create a map of previous alerts by key
      prev.forEach(prevAlert => {
        const key = getAlertKey(
          prevAlert.coin,
          prevAlert.strategyId,
          prevAlert.positionType,
          prevAlert.targetType,
          prevAlert.entryPrice,
          prevAlert.positionId
        );
        prevKeyMap.set(key, prevAlert);
      });
      
      // Build updated alerts array
      alertKeyMap.forEach((currentAlert, key) => {
        const existingAlert = prevKeyMap.get(key);
        if (existingAlert) {
          // Update existing alert: preserve original timestamp, update current data
          updatedAlerts.push({
            ...currentAlert,
            timestamp: existingAlert.timestamp // Keep original timestamp
          });
        } else {
          // New alert: use current timestamp
          updatedAlerts.push(currentAlert);
        }
      });
      
      return updatedAlerts;
      
      // Return updated alerts (only those currently within 1% threshold)
      return updatedAlerts;
    });
  };

  // Generate alerts for all positions (if showAllPositions is true)
  const getAllPositionAlerts = useMemo(() => {
    if (!showAllPositions) return [];
    
    // Collect all positions by strategy for basket TP calculation
    const positionsByStrategy = new Map<number, Array<{entryPrice: number, currentPrice: number, positionType: 'LONG' | 'SHORT', currentPnl: number}>>();
    liveData.forEach(coinData => {
      coinData.positions.forEach(position => {
        if (!positionsByStrategy.has(position.strategyId)) {
          positionsByStrategy.set(position.strategyId, []);
        }
        positionsByStrategy.get(position.strategyId)!.push({
          entryPrice: position.entryPrice,
          currentPrice: coinData.currentPrice,
          positionType: position.positionType,
          currentPnl: position.currentPnl
        });
      });
    });
    
    const allAlerts: Alert[] = [];
    liveData.forEach(coinData => {
      coinData.positions.forEach(position => {
        // US12: Leg-specific alerts - each alert is tied to a specific leg (LONG/SHORT) with its positionId
        if (position.takeProfitPrice && coinData.currentPrice) {
          const config = strategyConfigs.get(position.strategyId);
          const legIdentifier = position.positionType ? `[${position.positionType}]` : '';
          const positionIdSuffix = position.positionId ? ` (ID: ${position.positionId.substring(0, 8)}...)` : '';
          allAlerts.push({
            coin: coinData.coin,
            type: 'SELL',
            message: `${coinData.coin} ${legIdentifier} (${position.strategyName})${positionIdSuffix} - ${position.positionType || 'Position'} leg near Take Profit`,
            timestamp: new Date(),
            strategyName: position.strategyName,
            strategyId: position.strategyId,
            targetPrice: position.takeProfitPrice,
            currentPrice: coinData.currentPrice,
            targetType: 'TP',
            entryPrice: position.entryPrice,
            trailingStopLossPrice: position.trailingStopLossPrice,
            takeProfitPrice: position.takeProfitPrice,
            positionType: position.positionType,
            currentPnl: position.currentPnl,
            positionId: position.positionId, // US12: Unique identifier for this specific leg
            allPositionsForStrategy: positionsByStrategy.get(position.strategyId) || [],
            basketTakeProfitConfig: config?.basketTakeProfit,
            marketActivity24h: coinData.currentPrice
          });
        }
        
        // US12: Leg-specific alerts - each alert is tied to a specific leg (LONG/SHORT) with its positionId
        if (position.trailingStopLossPrice && coinData.currentPrice) {
          const config = strategyConfigs.get(position.strategyId);
          const legIdentifier = position.positionType ? `[${position.positionType}]` : '';
          const positionIdSuffix = position.positionId ? ` (ID: ${position.positionId.substring(0, 8)}...)` : '';
          allAlerts.push({
            coin: coinData.coin,
            type: 'SELL',
            message: `${coinData.coin} ${legIdentifier} (${position.strategyName})${positionIdSuffix} - ${position.positionType || 'Position'} leg near Trailing Stop Loss`,
            timestamp: new Date(),
            strategyName: position.strategyName,
            strategyId: position.strategyId,
            targetPrice: position.trailingStopLossPrice,
            currentPrice: coinData.currentPrice,
            targetType: 'TSL',
            entryPrice: position.entryPrice,
            trailingStopLossPrice: position.trailingStopLossPrice,
            takeProfitPrice: position.takeProfitPrice,
            positionType: position.positionType,
            currentPnl: position.currentPnl,
            positionId: position.positionId, // US12: Unique identifier for this specific leg
            allPositionsForStrategy: positionsByStrategy.get(position.strategyId) || [],
            basketTakeProfitConfig: config?.basketTakeProfit,
            marketActivity24h: coinData.currentPrice
          });
        }
      });
    });
    return allAlerts;
  }, [liveData, showAllPositions, strategyConfigs]);

  const baseDisplayAlerts = showAllPositions ? getAllPositionAlerts : alerts;
  
  // US15: Filter alerts by position type, symbol, and strategy
  const filteredAlerts = useMemo(() => {
    return baseDisplayAlerts.filter(alert => {
      // Filter by position type
      if (filterAlertType !== 'ALL' && alert.positionType !== filterAlertType) {
        return false;
      }
      // Filter by symbol (coin)
      if (filterSymbol && !alert.coin.toLowerCase().includes(filterSymbol.toLowerCase())) {
        return false;
      }
      // Filter by strategy
      if (filterStrategy && alert.strategyName && !alert.strategyName.toLowerCase().includes(filterStrategy.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [baseDisplayAlerts, filterAlertType, filterSymbol, filterStrategy]);
  
  // Sort alerts if a column is selected
  const displayAlerts = useMemo(() => {
    if (!alertSortColumn || filteredAlerts.length === 0) {
      return filteredAlerts;
    }
    
    return [...filteredAlerts].sort((a, b) => {
      let aValue: string;
      let bValue: string;
      
      switch (alertSortColumn) {
        case 'coin':
          aValue = a.coin || '';
          bValue = b.coin || '';
          break;
        case 'alertType':
          aValue = a.targetType || '';
          bValue = b.targetType || '';
          break;
        case 'type':
          aValue = a.positionType || '';
          bValue = b.positionType || '';
          break;
        case 'strategy':
          aValue = a.strategyName || '';
          bValue = b.strategyName || '';
          break;
        default:
          return 0;
      }
      
      const comparison = String(aValue).localeCompare(String(bValue));
      return alertSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredAlerts, alertSortColumn, alertSortDirection]);

  const formatPrice = (price: number): string => {
    if (price < 0.0001) {
      return price.toFixed(8);
    } else if (price < 1) {
      return price.toFixed(6);
    } else {
      return price.toFixed(4);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
             date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const calculatePnLPercent = (position: LiveCoinData['positions'][0], currentPrice: number): number => {
    if (!position) return 0;
    if (position.positionType === 'LONG') {
      return ((currentPrice - position.entryPrice) / position.entryPrice) * 100;
    } else {
      return ((position.entryPrice - currentPrice) / position.entryPrice) * 100;
    }
  };

  const formatPercent = (percent: number): string => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  const getPriceStatus = (currentPrice: number, targetPrice: number): 'at-target' | 'above' | 'below' => {
    if (!targetPrice) return 'at-target';
    const diff = Math.abs(currentPrice - targetPrice) / targetPrice * 100;
    if (diff <= 1.0) return 'at-target';
    return currentPrice > targetPrice ? 'above' : 'below';
  };

  const getStatusBadgeStyle = (status?: string) => {
    if (!status) return { backgroundColor: '#d1d5db', color: '#374151' };
    const statusUpper = status.toUpperCase();
    if (statusUpper === 'ACTIVE') {
      return { backgroundColor: '#d1fae5', color: '#065f46' };
    } else if (statusUpper === 'CLOSED') {
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    } else {
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    }
  };

  const loadHedgePositions = async (strategyId: number, strategyName: string) => {
    try {
      setHedgeLoading(true);
      setHedgeError(null);
      setHedgeStrategyName(strategyName);
      setHedgePositions(null);

      const resp = await adminApiService.getHedgePositionsForStrategy(strategyId);
      if (!resp.success) {
        setHedgeError(resp.error || 'Failed to load hedge positions');
        return;
      }
      setHedgePositions(resp.data || []);
    } catch (e: any) {
      setHedgeError(e?.message || 'Failed to load hedge positions');
    } finally {
      setHedgeLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#1a202c' }}>
          Live Market Data
        </h3>
        
        {/* Controls */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '12px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
              Market Type:
            </label>
            <select
              value={marketType}
              onChange={(e) => setMarketType(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="FUTURES">FUTURES</option>
              <option value="SPOT">SPOT</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
              Refresh:
            </label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="3000">3 seconds</option>
              <option value="5000">5 seconds</option>
              <option value="10000">10 seconds</option>
              <option value="30000">30 seconds</option>
            </select>
          </div>

          {(flags as any).enableHedgeModeUI && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                Hedge Mode view:
              </label>
              <input
                type="checkbox"
                checked={useHedgeView}
                onChange={(e) => setUseHedgeView(e.target.checked)}
              />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Experimental – uses per-leg hedge_positions
              </span>
            </div>
          )}
          
          {/* US10: Combined pair view toggle */}
          {useHedgeView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                Combined Pair View:
              </label>
              <input
                type="checkbox"
                checked={showCombinedPairView}
                onChange={(e) => setShowCombinedPairView(e.target.checked)}
              />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Show aggregated net hedged effect
              </span>
            </div>
          )}
          
          {/* US15: Filter controls for positions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
              Filter Positions:
            </label>
            <select
              value={filterPositionType}
              onChange={(e) => setFilterPositionType(e.target.value as 'ALL' | 'LONG' | 'SHORT')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All Types</option>
              <option value="LONG">LONG only</option>
              <option value="SHORT">SHORT only</option>
            </select>
            <input
              type="text"
              placeholder="Symbol..."
              value={filterSymbol}
              onChange={(e) => setFilterSymbol(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                width: '100px'
              }}
            />
            <input
              type="text"
              placeholder="Strategy..."
              value={filterStrategy}
              onChange={(e) => setFilterStrategy(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                width: '120px'
              }}
            />
          </div>
          
          {/* US15: Filter controls for alerts */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
              Filter Alerts:
            </label>
            <select
              value={filterAlertType}
              onChange={(e) => setFilterAlertType(e.target.value as 'ALL' | 'LONG' | 'SHORT')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="ALL">All Types</option>
              <option value="LONG">LONG only</option>
              <option value="SHORT">SHORT only</option>
            </select>
            {(filterSymbol || filterStrategy) && (
              <button
                onClick={() => {
                  setFilterSymbol('');
                  setFilterStrategy('');
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
          
          <button
            onClick={loadLiveData}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: loading ? '#9ca3af' : '#086afb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
          
          {lastRefresh && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginLeft: 'auto' }}>
              Last: {lastRefresh.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#991b1b',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Toggle Alerts Button - Show when alerts are hidden */}
      {!showAlerts && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setShowAlerts(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
          >
            <span>🔔</span>
            <span>Show Alerts Table</span>
            {displayAlerts.length > 0 && (
              <span style={{
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: 600
              }}>
                {displayAlerts.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Alerts Section - Table Format */}
      {showAlerts && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  🔔 {showAllPositions ? 'All Positions' : 'Active Alerts'} ({displayAlerts.length})
                </h4>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showAllPositions}
                    onChange={(e) => setShowAllPositions(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Show all positions
                </label>
              </div>
              <button
                onClick={() => setShowAlerts(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#6b7280',
                  padding: '0 4px',
                  lineHeight: '1'
                }}
                title="Hide alerts"
              >
                ×
              </button>
            </div>
            {!showAllPositions && (
              <p style={{ 
                margin: 0, 
                fontSize: '11px', 
                color: '#6b7280', 
                fontStyle: 'italic',
                paddingLeft: '4px'
              }}>
                Default alerts appear when prices are within 1% of Take Profit (TP) or Trailing Stop Loss (TSL) targets.
              </p>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#fef3c7', borderBottom: '2px solid #fde68a' }}>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: alertSortColumn === 'coin' ? '#fde68a' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleAlertSort('coin')}
                    onMouseEnter={(e) => {
                      if (alertSortColumn !== 'coin') {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (alertSortColumn !== 'coin') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Coin{renderAlertSortIndicator('coin')}
                  </th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: alertSortColumn === 'strategy' ? '#fde68a' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleAlertSort('strategy')}
                    onMouseEnter={(e) => {
                      if (alertSortColumn !== 'strategy') {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (alertSortColumn !== 'strategy') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Strategy{renderAlertSortIndicator('strategy')}
                  </th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: alertSortColumn === 'alertType' ? '#fde68a' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleAlertSort('alertType')}
                    onMouseEnter={(e) => {
                      if (alertSortColumn !== 'alertType') {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (alertSortColumn !== 'alertType') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Alert Type{renderAlertSortIndicator('alertType')}
                  </th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: alertSortColumn === 'type' ? '#fde68a' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleAlertSort('type')}
                    onMouseEnter={(e) => {
                      if (alertSortColumn !== 'type') {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (alertSortColumn !== 'type') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Type{renderAlertSortIndicator('type')}
                  </th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Entry Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Current Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Target Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Difference</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>TSL %</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>TP %</th>
                  <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>Trigger</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {(alertSortColumn ? displayAlerts : displayAlerts.slice().reverse()).map((alert: Alert, idx: number) => {
                  const priceDiff = alert.targetPrice && alert.currentPrice
                    ? Math.abs(alert.currentPrice - alert.targetPrice) / alert.targetPrice * 100
                    : 0;
                  const isAbove = alert.targetPrice && alert.currentPrice
                    ? alert.currentPrice > alert.targetPrice
                    : false;
                  
                  // Create a unique key for this row
                  const rowKey = `${alert.strategyName}-${idx}-${alert.coin}-${alert.timestamp.getTime()}`;
                  const shouldShowLogs = alert.strategyName && expandedLogsStrategy === alert.strategyName && expandedLogsRowKey === rowKey;
                  
                  return (
                    <React.Fragment key={idx}>
                      <tr
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: idx % 2 === 0 ? 'white' : '#fef9e7'
                        }}
                      >
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600, color: '#1a202c' }}>{alert.coin}</div>
                      </td>
                      <td style={{ padding: '8px', minWidth: isMobile ? '200px' : 'auto' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: isMobile ? 'flex-start' : 'center', 
                          gap: isMobile ? '4px' : '6px', 
                          flexWrap: 'wrap',
                          flexDirection: isMobile ? 'column' : 'row'
                        }}>
                          <div 
                            style={{ 
                              fontSize: isMobile ? '10px' : '11px', 
                              color: '#374151', 
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              flex: isMobile ? '0 0 auto' : 1,
                              minWidth: 0,
                              width: isMobile ? '100%' : 'auto',
                              marginBottom: isMobile ? '4px' : 0
                            }}
                            onMouseEnter={() => {
                              if (alert.strategyId && alert.strategyName) {
                                handleHoverStrategy(alert.strategyId, alert.strategyName);
                              }
                            }}
                            onMouseLeave={handleLeaveStrategy}
                            title="Hover to view strategy configuration"
                          >
                            {alert.strategyName || 'N/A'}
                          </div>
                          {alert.strategyName && (
                            <div style={{ 
                              display: 'flex', 
                              gap: isMobile ? '3px' : '4px', 
                              flexWrap: 'wrap',
                              width: isMobile ? '100%' : 'auto'
                            }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (alert.strategyName) {
                                    openLogsModal(alert.strategyName);
                                  }
                                }}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title="View logs in modal"
                              >
                                📋 Logs
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (alert.strategyName) {
                                    const rowKey = `${alert.strategyName}-${idx}-${alert.coin}-${alert.timestamp.getTime()}`;
                                    // US13: Pass leg-specific filters when expanding logs
                                    const filters = {
                                      symbol: alert.coin,
                                      side: alert.positionType,
                                      positionId: alert.positionId || undefined
                                    };
                                    toggleExpandedLogs(alert.strategyName, rowKey, filters);
                                  }
                                }}
                                style={{
                                  background: expandedLogsStrategy === alert.strategyName ? '#667eea' : '#f3f4f6',
                                  color: expandedLogsStrategy === alert.strategyName ? 'white' : '#374151',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title={expandedLogsStrategy === alert.strategyName ? "Hide inline logs" : "Show inline logs"}
                              >
                                {expandedLogsStrategy === alert.strategyName ? '▼' : '▶'} Logs
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: alert.targetType === 'TP' ? '#dbeafe' : '#fee2e2',
                          color: alert.targetType === 'TP' ? '#1e40af' : '#991b1b'
                        }}>
                          {alert.targetType === 'TP' ? 'Take Profit' : 'Stop Loss'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: alert.positionType === 'LONG' ? '#dbeafe' : '#e9d5ff',
                          color: alert.positionType === 'LONG' ? '#1e40af' : '#6b21a8'
                        }}>
                          {alert.positionType || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: '#374151' }}>
                          {alert.entryPrice ? `$${formatPrice(alert.entryPrice)}` : 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: '#1a202c' }}>
                          ${alert.currentPrice ? formatPrice(alert.currentPrice) : 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ 
                          fontWeight: 600, 
                          color: alert.targetType === 'TP' ? '#1e40af' : '#991b1b'
                        }}>
                          ${alert.targetPrice ? formatPrice(alert.targetPrice) : 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {(() => {
                          if (alert.targetPrice && alert.currentPrice) {
                            const diffFormula = isAbove
                              ? `((Current - Target) / Target) × 100\n= ((${formatPrice(alert.currentPrice)} - ${formatPrice(alert.targetPrice)}) / ${formatPrice(alert.targetPrice)}) × 100\n= ${formatPercent(priceDiff)}`
                              : `((Target - Current) / Target) × 100\n= ((${formatPrice(alert.targetPrice)} - ${formatPrice(alert.currentPrice)}) / ${formatPrice(alert.targetPrice)}) × 100\n= ${formatPercent(priceDiff)}`;
                            
                            return (
                              <div>
                                <div 
                                  style={{ 
                                    fontWeight: 500,
                                    color: priceDiff <= 0.5 ? '#d97706' : isAbove ? '#059669' : '#dc2626',
                                    cursor: isMobile ? 'pointer' : 'help',
                                    userSelect: 'none'
                                  }}
                                  title={!isMobile ? diffFormula : undefined}
                                  onClick={(e) => handleTooltipClick(e, diffFormula)}
                                >
                                  {formatPercent(isAbove ? priceDiff : -priceDiff)}
                                </div>
                                <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                                  {priceDiff <= 0.5 ? 'At target' : isAbove ? 'Above' : 'Below'}
                                </div>
                              </div>
                            );
                          }
                          return <div style={{ fontWeight: 500, color: '#9ca3af' }}>N/A</div>;
                        })()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {(() => {
                          // Calculate actual TSL %: difference from current price to TSL target
                          if (alert.trailingStopLossPrice && alert.currentPrice) {
                            const tslDiff = alert.positionType === 'LONG'
                              ? ((alert.currentPrice - alert.trailingStopLossPrice) / alert.trailingStopLossPrice) * 100
                              : ((alert.trailingStopLossPrice - alert.currentPrice) / alert.trailingStopLossPrice) * 100;
                            
                            const formula = alert.positionType === 'LONG'
                              ? `((Current - TSL) / TSL) × 100\n= ((${formatPrice(alert.currentPrice)} - ${formatPrice(alert.trailingStopLossPrice)}) / ${formatPrice(alert.trailingStopLossPrice)}) × 100\n= ${formatPercent(tslDiff)}`
                              : `((TSL - Current) / TSL) × 100\n= ((${formatPrice(alert.trailingStopLossPrice)} - ${formatPrice(alert.currentPrice)}) / ${formatPrice(alert.trailingStopLossPrice)}) × 100\n= ${formatPercent(tslDiff)}`;
                            
                            return (
                              <div 
                                style={{ 
                                  fontWeight: 500, 
                                  color: '#991b1b', 
                                  cursor: isMobile ? 'pointer' : 'help',
                                  userSelect: 'none'
                                }}
                                title={!isMobile ? formula : undefined}
                                onClick={(e) => handleTooltipClick(e, formula)}
                              >
                                {formatPercent(tslDiff)}
                              </div>
                            );
                          }
                          return <div style={{ fontWeight: 500, color: '#9ca3af' }}>N/A</div>;
                        })()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {(() => {
                          // Calculate actual TP %: difference from current price to TP target
                          if (alert.takeProfitPrice && alert.currentPrice) {
                            const tpDiff = alert.positionType === 'LONG'
                              ? ((alert.takeProfitPrice - alert.currentPrice) / alert.currentPrice) * 100
                              : ((alert.currentPrice - alert.takeProfitPrice) / alert.currentPrice) * 100;
                            
                            const formula = alert.positionType === 'LONG'
                              ? `((TP - Current) / Current) × 100\n= ((${formatPrice(alert.takeProfitPrice)} - ${formatPrice(alert.currentPrice)}) / ${formatPrice(alert.currentPrice)}) × 100\n= ${formatPercent(tpDiff)}`
                              : `((Current - TP) / Current) × 100\n= ((${formatPrice(alert.currentPrice)} - ${formatPrice(alert.takeProfitPrice)}) / ${formatPrice(alert.currentPrice)}) × 100\n= ${formatPercent(tpDiff)}`;
                            
                            return (
                              <div 
                                style={{ 
                                  fontWeight: 500, 
                                  color: '#1e40af', 
                                  cursor: isMobile ? 'pointer' : 'help',
                                  userSelect: 'none'
                                }}
                                title={!isMobile ? formula : undefined}
                                onClick={(e) => handleTooltipClick(e, formula)}
                              >
                                {formatPercent(tpDiff)}
                              </div>
                            );
                          }
                          return <div style={{ fontWeight: 500, color: '#9ca3af' }}>N/A</div>;
                        })()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'left' }}>
                        {(() => {
                          if (!alert.targetPrice || !alert.currentPrice || !alert.entryPrice) {
                            return <div style={{ fontWeight: 500, color: '#9ca3af', fontSize: '11px' }}>N/A</div>;
                          }
                          
                          // Determine if trigger condition is met
                          const isTriggered = alert.targetType === 'TP'
                            ? (alert.positionType === 'LONG' 
                                ? alert.currentPrice >= alert.targetPrice 
                                : alert.currentPrice <= alert.targetPrice)
                            : (alert.positionType === 'LONG'
                                ? alert.currentPrice <= alert.targetPrice
                                : alert.currentPrice >= alert.targetPrice);
                          
                          // Calculate profit for Micro TSL check
                          const positionType = alert.positionType || 'LONG';
                          const profitPercent = positionType === 'LONG'
                            ? ((alert.currentPrice - alert.entryPrice) / alert.entryPrice) * 100
                            : ((alert.entryPrice - alert.currentPrice) / alert.entryPrice) * 100;
                          
                          // Check Micro TSL status
                          const config = alert.strategyId ? strategyConfigs.get(alert.strategyId) : null;
                          const microTslEnabled = config?.enableMicroTsl || false;
                          const microTslActivation = config?.microTslActivationPercent || 0;
                          const isMicroTslActive = microTslEnabled && profitPercent >= microTslActivation;
                          
                          // Check TP threshold (only for TP alerts)
                          const tpThreshold = config?.takeProfit || null;
                          const tpConditionMet = alert.targetType === 'TP' && tpThreshold !== null
                            ? profitPercent >= tpThreshold
                            : null;
                          
                          return (
                            <div
                              style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontSize: '11px',
                                lineHeight: '1.6'
                              }}
                              onClick={() => {
                                setSelectedTriggerAlert(alert);
                                setTriggerModalOpen(true);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                            >
                              <div style={{ marginBottom: '4px' }}>
                                <span style={{ fontWeight: 600, color: '#374151' }}>Trigger Condition:</span>{' '}
                                <span style={{ 
                                  fontWeight: 600,
                                  color: isTriggered ? '#059669' : '#dc2626'
                                }}>
                                  {isTriggered ? 'Yes' : 'No'}
                                </span>
                              </div>
                              
                              {alert.targetType === 'TP' && tpThreshold !== null && (
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 600, color: '#374151' }}>TP Threshold:</span>{' '}
                                  <span style={{ 
                                    fontWeight: 600,
                                    color: tpConditionMet ? '#059669' : '#dc2626'
                                  }}>
                                    {tpConditionMet ? 'Met' : 'Not Met'}
                                  </span>
                                </div>
                              )}
                              
                              {microTslEnabled && (
                                <div>
                                  <span style={{ fontWeight: 600, color: '#374151' }}>Micro TSL:</span>{' '}
                                  <span style={{ 
                                    fontWeight: 600,
                                    color: isMicroTslActive ? '#059669' : '#dc2626'
                                  }}>
                                    {isMicroTslActive ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {alert.timestamp.toLocaleTimeString()}
                        </div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                          {alert.timestamp.toLocaleDateString()}
                        </div>
                      </td>
                    </tr>
                    {/* Expandable Inline Logs Section - Only show once per strategy */}
                    {shouldShowLogs && (
                      <tr>
                        <td colSpan={12} style={{ padding: '0', backgroundColor: idx % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
                          <div style={{
                            padding: '12px',
                            borderTop: '2px solid #e5e7eb',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: '#1e293b',
                            maxHeight: '400px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            lineHeight: '1.5'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '8px',
                              paddingBottom: '8px',
                              borderBottom: '1px solid #334155'
                            }}>
                              <div style={{ color: '#94a3b8', fontWeight: 600 }}>
                                📋 Live Logs: {alert.strategyName}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                  onClick={() => {
                                    if (alert.strategyName) {
                                      // US13: Calculate the same logKey that was used when storing logs (includes filters)
                                      const filters = {
                                        symbol: alert.coin,
                                        side: alert.positionType,
                                        positionId: alert.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${alert.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : alert.strategyName;
                                      const logsData = inlineLogs.get(logKey);
                                      if (logsData) {
                                        setInlineLogs(prev => {
                                          const newMap = new Map(prev);
                                          const current = newMap.get(logKey)!;
                                          newMap.set(logKey, { ...current, autoRefresh: !current.autoRefresh });
                                          return newMap;
                                        });
                                      }
                                    }
                                  }}
                                  style={{
                                    background: (() => {
                                      if (!alert.strategyName) return '#6b7280';
                                      const filters = {
                                        symbol: alert.coin,
                                        side: alert.positionType,
                                        positionId: alert.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${alert.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : alert.strategyName;
                                      return inlineLogs.get(logKey)?.autoRefresh ? '#10b981' : '#6b7280';
                                    })(),
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {(() => {
                                    if (!alert.strategyName) return '▶ Resume';
                                    const filters = {
                                      symbol: alert.coin,
                                      side: alert.positionType,
                                      positionId: alert.positionId || undefined
                                    };
                                    const logKey = filters 
                                      ? `${alert.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                      : alert.strategyName;
                                    return inlineLogs.get(logKey)?.autoRefresh ? '⏸ Pause' : '▶ Resume';
                                  })()}
                                </button>
                                <button
                                  onClick={() => {
                                    if (alert.strategyName) {
                                      // US13: Pass leg-specific filters when available
                                      const filters = {
                                        symbol: alert.coin,
                                        side: alert.positionType,
                                        positionId: alert.positionId || undefined
                                      };
                                      fetchInlineLogs(alert.strategyName, 200, filters);
                                    }
                                  }}
                                  style={{
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  🔄 Refresh
                                </button>
                                <button
                                  onClick={() => {
                                    if (alert.strategyName) {
                                      // US13: Calculate the same logKey that was used when storing logs (includes filters)
                                      const filters = {
                                        symbol: alert.coin,
                                        side: alert.positionType,
                                        positionId: alert.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${alert.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : alert.strategyName;
                                      const logsData = inlineLogs.get(logKey);
                                      if (logsData?.logs) {
                                        navigator.clipboard.writeText(logsData.logs.join('\n'));
                                        window.alert('Logs copied to clipboard!');
                                      }
                                    }
                                  }}
                                  style={{
                                    background: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                            {(() => {
                              if (!alert.strategyName) return null;
                              // US13: Calculate the same logKey that was used when storing logs (includes filters)
                              const filters = {
                                symbol: alert.coin,
                                side: alert.positionType,
                                positionId: alert.positionId || undefined
                              };
                              const logKey = filters 
                                ? `${alert.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                : alert.strategyName;
                              const logsData = inlineLogs.get(logKey);
                              if (logsData?.loading) {
                                return (
                                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                    Loading logs...
                                  </div>
                                );
                              }
                              if (logsData?.error) {
                                return (
                                  <div style={{ color: '#ef4444', padding: '12px', backgroundColor: '#7f1d1d', borderRadius: '4px' }}>
                                    Error: {logsData.error}
                                  </div>
                                );
                              }
                              if (logsData?.logs && logsData.logs.length > 0) {
                                return (
                                  <div>
                                    {logsData.logs.map((log, logIdx) => {
                                      // Color code log levels
                                      let logColor = '#94a3b8'; // Default gray
                                      if (log.includes('ERROR') || log.includes('Exception') || log.includes('Failed')) {
                                        logColor = '#ef4444'; // Red
                                      } else if (log.includes('WARN') || log.includes('Warning')) {
                                        logColor = '#f59e0b'; // Orange
                                      } else if (log.includes('INFO') || log.includes('Success')) {
                                        logColor = '#10b981'; // Green
                                      } else if (log.includes('DEBUG')) {
                                        logColor = '#3b82f6'; // Blue
                                      }
                                      
                                      return (
                                        <div 
                                          key={logIdx}
                                          style={{ 
                                            color: logColor,
                                            marginBottom: '2px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {log}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              return (
                                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                  No logs available
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty Alerts State - Show when alerts section is visible but no alerts */}
      {showAlerts && displayAlerts.length === 0 && (
        <div style={{ 
          marginBottom: '20px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔔</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
            No Active Alerts
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {showAllPositions 
              ? 'No positions found. Alerts will appear when positions are within 1% of TP or TSL targets.'
              : 'No alerts within 1% of TP or TSL targets. Check "Show all positions" to see all positions.'}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && liveData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '16px', color: '#666' }}>Loading live data...</div>
        </div>
      )}

      {/* US10: Combined Pair View Table */}
      {showCombinedPairView && combinedPairRows && combinedPairRows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
            Combined Hedged Pairs ({combinedPairRows.length} {combinedPairRows.length === 1 ? 'pair' : 'pairs'})
            <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px', fontWeight: 'normal' }}>
              (Net hedged effect: LONG + SHORT on same symbol)
            </span>
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Coin</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Price</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Strategy</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Account</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>LONG PnL</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>SHORT PnL</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, backgroundColor: '#dbeafe' }}>Net PnL</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>LONG Entry</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>SHORT Entry</th>
                </tr>
              </thead>
              <tbody>
                {combinedPairRows.map((pair, index) => {
                  const netPnlPercent = pair.combinedExposure > 0 
                    ? ((pair.netPnl / pair.combinedExposure) * 100)
                    : 0;
                  
                  return (
                    <tr
                      key={`pair-${pair.coin}-${pair.strategyId}`}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600, color: '#1a202c' }}>{pair.coin}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                          {pair.symbol}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: '#1a202c' }}>
                          ${formatPrice(pair.currentPrice)}
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontSize: '11px', color: '#374151', fontWeight: 500 }}>
                          {pair.strategyName}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'left' }}>
                        {(() => {
                          const accountName = getAccountName(pair.strategyId);
                          const isShared = accountName === 'Shared KuCoin (System)';
                          return (
                            <span style={{ 
                              fontSize: 12, 
                              color: isShared ? '#4b5563' : '#111827', 
                              fontWeight: isShared ? 400 : 500 
                            }}>
                              {accountName}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ 
                          fontWeight: 600, 
                          color: pair.longPnl >= 0 ? '#059669' : '#dc2626' 
                        }}>
                          ${pair.longPnl.toFixed(2)}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ 
                          fontWeight: 600, 
                          color: pair.shortPnl >= 0 ? '#059669' : '#dc2626' 
                        }}>
                          ${pair.shortPnl.toFixed(2)}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', backgroundColor: '#eff6ff' }}>
                        <div style={{ 
                          fontWeight: 700, 
                          color: pair.netPnl >= 0 ? '#059669' : '#dc2626',
                          fontSize: '13px'
                        }}>
                          ${pair.netPnl.toFixed(2)}
                        </div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: pair.netPnl >= 0 ? '#059669' : '#dc2626',
                          marginTop: '2px'
                        }}>
                          {formatPercent(netPnlPercent)}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          ${formatPrice(pair.longEntryPrice)}
                        </div>
                        {pair.longPositionId && (
                          <div style={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {pair.longPositionId.substring(0, 8)}...
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          ${formatPrice(pair.shortEntryPrice)}
                        </div>
                        {pair.shortPositionId && (
                          <div style={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {pair.shortPositionId.substring(0, 8)}...
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Live Data Table */}
      {tableRows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
            {showCombinedPairView && useHedgeView ? 'Individual Legs' : 'Live Positions'} ({tableRows.length} {tableRows.length === 1 ? 'position' : 'positions'})
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Coin</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Current Price</th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: sortColumn === 'date' ? '#e5e7eb' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleSort('date')}
                    onMouseEnter={(e) => {
                      if (sortColumn !== 'date') {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortColumn !== 'date') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Date{renderSortIndicator('date')}
                  </th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: sortColumn === 'type' ? '#e5e7eb' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleSort('type')}
                    onMouseEnter={(e) => {
                      if (sortColumn !== 'type') {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortColumn !== 'type') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Type{renderSortIndicator('type')}
                  </th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: sortColumn === 'status' ? '#e5e7eb' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => handleSort('status')}
                    onMouseEnter={(e) => {
                      if (sortColumn !== 'status') {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortColumn !== 'status') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Status{renderSortIndicator('status')}
                  </th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Position ID</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Close ID</th>
                  <th 
                    style={{ 
                      padding: '8px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: sortColumn === 'strategy' ? '#e5e7eb' : 'transparent',
                      transition: 'background-color 0.2s',
                      minWidth: isMobile ? '200px' : 'auto'
                    }}
                    onClick={() => handleSort('strategy')}
                    onMouseEnter={(e) => {
                      if (sortColumn !== 'strategy') {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (sortColumn !== 'strategy') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    Strategy{renderSortIndicator('strategy')}
                  </th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Account</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Entry Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>PnL</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Take Profit</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Stop Loss</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, index) => {
                  if (!row.position) {
                    return (
                      <tr 
                        key={`${row.coin}-no-pos`}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                        }}
                      >
                        <td style={{ padding: '8px' }}>
                          <div style={{ fontWeight: 600, color: '#1a202c' }}>{row.coin}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                            {row.symbol}
                          </div>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          <div style={{ fontWeight: 500, color: '#1a202c' }}>
                            ${formatPrice(row.currentPrice)}
                          </div>
                        </td>
                        <td colSpan={11} style={{ padding: '8px', color: '#9ca3af', fontStyle: 'italic' }}>
                          No active positions
                        </td>
                      </tr>
                    );
                  }
                  
                  // Create a unique key for this row
                  const rowKey = `${row.position.strategyName}-${index}-${row.coin}-${row.position.positionId || row.position.entryDate || Date.now()}`;
                  const shouldShowLogs = row.position.strategyName && expandedLogsStrategy === row.position.strategyName && expandedLogsRowKey === rowKey;
                  
                  const pnlPercent = calculatePnLPercent(row.position, row.currentPrice);
                  const tpStatus = row.position.takeProfitPrice ? getPriceStatus(row.currentPrice, row.position.takeProfitPrice) : null;
                  const tslStatus = row.position.trailingStopLossPrice ? getPriceStatus(row.currentPrice, row.position.trailingStopLossPrice) : null;
                  const statusStyle = getStatusBadgeStyle(row.position.status);
                  
                  return (
                    <React.Fragment key={`${row.coin}-${row.position.positionId || index}`}>
                      <tr 
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                        }}
                      >
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600, color: '#1a202c' }}>{row.coin}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                          {row.symbol}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: '#1a202c' }}>
                          ${formatPrice(row.currentPrice)}
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontSize: '11px', color: '#374151' }}>
                          {formatDate(row.position.entryDate)}
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          backgroundColor: row.position.positionType === 'LONG' ? '#dbeafe' : '#e9d5ff',
                          color: row.position.positionType === 'LONG' ? '#1e40af' : '#6b21a8'
                        }}>
                          {row.position.positionType}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600,
                          ...statusStyle
                        }}>
                          {row.position.status || 'ACTIVE'}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#6b7280' }}>
                          {row.position.positionId || 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#6b7280' }}>
                          {row.position.closeOrderId || 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '8px', minWidth: isMobile ? '200px' : 'auto' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: isMobile ? 'flex-start' : 'center', 
                          gap: isMobile ? '4px' : '6px', 
                          flexWrap: 'wrap',
                          flexDirection: isMobile ? 'column' : 'row'
                        }}>
                          <div 
                            style={{ 
                              fontSize: isMobile ? '10px' : '11px', 
                              color: '#374151', 
                              fontWeight: 500,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              flex: isMobile ? '0 0 auto' : 1,
                              minWidth: 0,
                              width: isMobile ? '100%' : 'auto',
                              marginBottom: isMobile ? '4px' : 0
                            }}
                            onMouseEnter={() => {
                              if (row.position.strategyId) {
                                handleHoverStrategy(row.position.strategyId, row.position.strategyName);
                              }
                            }}
                            onMouseLeave={handleLeaveStrategy}
                            title="Hover to view strategy configuration"
                          >
                            {row.position.strategyName}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            gap: isMobile ? '3px' : '4px', 
                            flexWrap: 'wrap',
                            width: isMobile ? '100%' : 'auto'
                          }}>
                            {row.position.strategyName && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (row.position.strategyName) {
                                    openLogsModal(row.position.strategyName);
                                  }
                                }}
                                style={{
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title="View logs in modal"
                              >
                                📋 Logs
                              </button>
                            )}
                            {row.position.strategyId && row.position.strategyName && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadHedgePositions(row.position.strategyId!, row.position.strategyName!);
                                }}
                                style={{
                                  background: '#0ea5e9',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title="View hedge legs for this strategy"
                              >
                                󰓅 Hedge
                              </button>
                            )}
                            {/* Close button - for hedge legs use closeHedgeLeg, for regular positions use closePosition */}
                            {row.position.positionId && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!row.position.positionId || !row.symbol) return;
                                  
                                  const confirmed = window.confirm(
                                    `Close this ${row.position.positionType} position for ${row.coin}?\n\nPosition ID: ${row.position.positionId}\nSymbol: ${row.symbol}`
                                  );
                                  if (!confirmed) return;
                                  
                                  try {
                                    let resp;
                                    // If it's a hedge leg, use closeHedgeLeg API
                                    if (useHedgeView && (row.position as any).hedgePositionId) {
                                      const legId = (row.position as any).hedgePositionId as number;
                                      resp = await adminApiService.closeHedgeLeg(legId, `ADMIN_CLOSE_FROM_LIVE_POSITIONS_${row.position.positionType}`);
                                    } else {
                                      // Regular position - use closePosition API
                                      resp = await adminApiService.closePosition(
                                        row.position.positionId,
                                        row.symbol,
                                        row.position.positionType,
                                        `ADMIN_CLOSE_FROM_LIVE_POSITIONS_${row.position.positionType}`
                                      );
                                    }
                                    
                                    if (!resp.success) {
                                      window.alert(resp.error || 'Failed to close position');
                                    } else {
                                      // Refresh live data to reflect the closed position
                                      loadLiveData();
                                    }
                                  } catch (err: any) {
                                    window.alert(err?.message || 'Failed to close position');
                                  }
                                }}
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title={useHedgeView && (row.position as any).hedgePositionId 
                                  ? "Close this hedge leg" 
                                  : "Close this position"}
                              >
                                ✖ Close
                              </button>
                            )}
                            {row.position.strategyName && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (row.position.strategyName) {
                                    const rowKey = `${row.position.strategyName}-${index}-${row.coin}-${row.position.positionId || row.position.entryDate || Date.now()}`;
                                    // US13: Pass leg-specific filters when expanding logs
                                    const filters = {
                                      symbol: row.symbol,
                                      side: row.position.positionType,
                                      positionId: row.position.positionId || undefined
                                    };
                                    toggleExpandedLogs(row.position.strategyName, rowKey, filters);
                                  }
                                }}
                                style={{
                                  background: expandedLogsStrategy === row.position.strategyName ? '#667eea' : '#f3f4f6',
                                  color: expandedLogsStrategy === row.position.strategyName ? 'white' : '#374151',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: isMobile ? '3px 5px' : '2px 6px',
                                  fontSize: isMobile ? '9px' : '10px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                title={expandedLogsStrategy === row.position.strategyName ? "Hide inline logs" : "Show inline logs"}
                              >
                                {expandedLogsStrategy === row.position.strategyName ? '▼' : '▶'} Logs
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'left' }}>
                        {(() => {
                          const accountName = getAccountName(row.position.strategyId);
                          const isShared = accountName === 'Shared KuCoin (System)';
                          return (
                            <span style={{ 
                              fontSize: 12, 
                              color: isShared ? '#4b5563' : '#111827', 
                              fontWeight: isShared ? 400 : 500 
                            }}>
                              {accountName}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ fontWeight: 500, color: '#1a202c' }}>
                          ${formatPrice(row.position.entryPrice)}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <div style={{ 
                          fontWeight: 600,
                          color: pnlPercent >= 0 ? '#059669' : '#dc2626'
                        }}>
                          {formatPercent(pnlPercent)}
                        </div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                          ${row.position.currentPnl.toFixed(2)}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {row.position.takeProfitPrice ? (
                          <>
                            <div style={{
                              fontWeight: tpStatus === 'at-target' ? 700 : 500,
                              color: tpStatus === 'at-target' ? '#d97706' : tpStatus === 'above' ? '#059669' : '#6b7280'
                            }}>
                              ${formatPrice(row.position.takeProfitPrice)}
                            </div>
                            <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                              ({row.position.takeProfitPercent}%)
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '11px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {row.position.trailingStopLossPrice ? (
                          <>
                            <div style={{
                              fontWeight: tslStatus === 'at-target' ? 700 : 500,
                              color: tslStatus === 'at-target' ? '#d97706' : tslStatus === 'below' ? '#dc2626' : '#6b7280'
                            }}>
                              ${formatPrice(row.position.trailingStopLossPrice)}
                            </div>
                            <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                              ({row.position.trailingStopLossPercent}%)
                            </div>
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '11px' }}>-</span>
                        )}
                      </td>
                    </tr>
                    {/* Expandable Inline Logs Section - Only show once per strategy */}
                    {shouldShowLogs && (
                      <tr>
                        <td colSpan={12} style={{ padding: '0', backgroundColor: index % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
                          <div style={{
                            padding: '12px',
                            borderTop: '2px solid #e5e7eb',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: '#1e293b',
                            maxHeight: '400px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            lineHeight: '1.5'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '8px',
                              paddingBottom: '8px',
                              borderBottom: '1px solid #334155'
                            }}>
                              <div style={{ color: '#94a3b8', fontWeight: 600 }}>
                                📋 Live Logs: {row.position.strategyName}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                  onClick={() => {
                                    if (row.position.strategyName) {
                                      // US13: Calculate the same logKey that was used when storing logs (includes filters)
                                      const filters = {
                                        symbol: row.symbol,
                                        side: row.position.positionType,
                                        positionId: row.position.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${row.position.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : row.position.strategyName;
                                      const logsData = inlineLogs.get(logKey);
                                      if (logsData) {
                                        setInlineLogs(prev => {
                                          const newMap = new Map(prev);
                                          const current = newMap.get(logKey)!;
                                          newMap.set(logKey, { ...current, autoRefresh: !current.autoRefresh });
                                          return newMap;
                                        });
                                      }
                                    }
                                  }}
                                  style={{
                                    background: (() => {
                                      if (!row.position.strategyName) return '#6b7280';
                                      const filters = {
                                        symbol: row.symbol,
                                        side: row.position.positionType,
                                        positionId: row.position.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${row.position.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : row.position.strategyName;
                                      return inlineLogs.get(logKey)?.autoRefresh ? '#10b981' : '#6b7280';
                                    })(),
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {(() => {
                                    if (!row.position.strategyName) return '▶ Resume';
                                    const filters = {
                                      symbol: row.symbol,
                                      side: row.position.positionType,
                                      positionId: row.position.positionId || undefined
                                    };
                                    const logKey = filters 
                                      ? `${row.position.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                      : row.position.strategyName;
                                    return inlineLogs.get(logKey)?.autoRefresh ? '⏸ Pause' : '▶ Resume';
                                  })()}
                                </button>
                                <button
                                  onClick={() => {
                                    if (row.position.strategyName) {
                                      // US13: Pass leg-specific filters when available
                                      const filters = {
                                        symbol: row.symbol,
                                        side: row.position.positionType,
                                        positionId: row.position.positionId || undefined
                                      };
                                      fetchInlineLogs(row.position.strategyName, 200, filters);
                                    }
                                  }}
                                  style={{
                                    background: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  🔄 Refresh
                                </button>
                                <button
                                  onClick={() => {
                                    if (row.position.strategyName) {
                                      // US13: Calculate the same logKey that was used when storing logs (includes filters)
                                      const filters = {
                                        symbol: row.symbol,
                                        side: row.position.positionType,
                                        positionId: row.position.positionId || undefined
                                      };
                                      const logKey = filters 
                                        ? `${row.position.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                        : row.position.strategyName;
                                      const logsData = inlineLogs.get(logKey);
                                      if (logsData?.logs) {
                                        navigator.clipboard.writeText(logsData.logs.join('\n'));
                                        window.alert('Logs copied to clipboard!');
                                      }
                                    }
                                  }}
                                  style={{
                                    background: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  📋 Copy
                                </button>
                              </div>
                            </div>
                            {(() => {
                              if (!row.position.strategyName) return null;
                              // US13: Calculate the same logKey that was used when storing logs (includes filters)
                              const filters = {
                                symbol: row.symbol,
                                side: row.position.positionType,
                                positionId: row.position.positionId || undefined
                              };
                              const logKey = filters 
                                ? `${row.position.strategyName}-${filters.symbol || ''}-${filters.side || ''}-${filters.positionId || ''}`
                                : row.position.strategyName;
                              const logsData = inlineLogs.get(logKey);
                              if (logsData?.loading) {
                                return (
                                  <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                    Loading logs...
                                  </div>
                                );
                              }
                              if (logsData?.error) {
                                return (
                                  <div style={{ color: '#ef4444', padding: '12px', backgroundColor: '#7f1d1d', borderRadius: '4px' }}>
                                    Error: {logsData.error}
                                  </div>
                                );
                              }
                              if (logsData?.logs && logsData.logs.length > 0) {
                                return (
                                  <div>
                                    {logsData.logs.map((log, logIdx) => {
                                      // Color code log levels
                                      let logColor = '#94a3b8'; // Default gray
                                      if (log.includes('ERROR') || log.includes('Exception') || log.includes('Failed')) {
                                        logColor = '#ef4444'; // Red
                                      } else if (log.includes('WARN') || log.includes('Warning')) {
                                        logColor = '#f59e0b'; // Orange
                                      } else if (log.includes('INFO') || log.includes('Success')) {
                                        logColor = '#10b981'; // Green
                                      } else if (log.includes('DEBUG')) {
                                        logColor = '#3b82f6'; // Blue
                                      }
                                      
                                      return (
                                        <div 
                                          key={logIdx}
                                          style={{ 
                                            color: logColor,
                                            marginBottom: '2px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {log}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              return (
                                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                  No logs available
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hedge positions inspector */}
      {(hedgeLoading || hedgePositions || hedgeError) && (
        <div
          style={{
            marginTop: '24px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            padding: '12px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h4
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Hedge Positions {hedgeStrategyName ? `– ${hedgeStrategyName}` : ''}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {hedgePositions && (
                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                  {hedgePositions.length} leg(s)
                </span>
              )}
              <button
                onClick={() => {
                  setHedgePositions(null);
                  setHedgeLoading(false);
                  setHedgeError(null);
                  setHedgeStrategyName('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
                title="Close hedge positions view"
              >
                ×
              </button>
            </div>
          </div>

          {hedgeLoading && (
            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
              Loading hedge positions…
            </p>
          )}

          {hedgeError && !hedgeLoading && (
            <p style={{ fontSize: '11px', color: '#b91c1c', margin: 0 }}>
              {hedgeError}
            </p>
          )}

          {!hedgeLoading && hedgePositions && hedgePositions.length > 0 && (
            <div style={{ marginTop: '8px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Symbol</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Side</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>Size</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>Entry</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>PnL</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Status</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Opened</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Closed</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hedgePositions.map((hp: any) => (
                    <tr key={hp.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '6px 8px' }}>{hp.symbol}</td>
                      <td style={{ padding: '6px 8px' }}>{hp.side}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {hp.size != null ? hp.size : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {hp.entryPrice != null ? hp.entryPrice : '-'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {hp.pnl != null ? hp.pnl : '-'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>{hp.status}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {hp.createdAt ? new Date(hp.createdAt).toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {hp.closedAt ? new Date(hp.closedAt).toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {hp.status === 'OPEN' && hp.positionId ? (
                          <button
                            onClick={async () => {
                              const confirmed = window.confirm(
                                `Close hedge leg ${hp.id} (${hp.symbol} ${hp.side})?`
                              );
                              if (!confirmed) return;
                              try {
                                const resp = await adminApiService.closeHedgeLeg(
                                  hp.id,
                                  `ADMIN_CLOSE_FROM_HEDGE_INSPECTOR_${hp.side}`
                                );
                                if (!resp.success) {
                                  window.alert(resp.error || 'Failed to close hedge leg');
                                } else {
                                  // Reload hedge positions for the same strategy
                                  if (hp.strategyId && hedgeStrategyName) {
                                    loadHedgePositions(hp.strategyId, hedgeStrategyName);
                                  }
                                  // Also refresh main live data
                                  loadLiveData();
                                }
                              } catch (e: any) {
                                window.alert(e?.message || 'Failed to close hedge leg');
                              }
                            }}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '10px',
                              cursor: 'pointer'
                            }}
                          >
                            ✖ Close Leg
                          </button>
                        ) : (
                          <span style={{ fontSize: '10px', color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!hedgeLoading && hedgePositions && hedgePositions.length === 0 && !hedgeError && (
            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
              No hedge legs found for this strategy.
            </p>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && tableRows.length === 0 && !error && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
            No live data available
          </div>
          <div style={{ fontSize: '14px', color: '#9ca3af' }}>
            No active strategies or positions found.
          </div>
        </div>
      )}

      {/* Mobile Tooltip */}
      {tooltipContent && tooltipPosition && isMobile && (
        <div
          style={{
            position: 'fixed',
            left: `${Math.max(10, Math.min(tooltipPosition.x - 150, window.innerWidth - 310))}px`,
            top: `${Math.max(10, tooltipPosition.y - 100)}px`,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '12px',
            lineHeight: '1.5',
            zIndex: 10001,
            maxWidth: '300px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            whiteSpace: 'pre-line',
            pointerEvents: 'none'
          }}
        >
          {tooltipContent}
        </div>
      )}

      {/* Strategy Configuration Modal */}
      <StrategyConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedStrategyId(null);
          setSelectedStrategyName('');
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
        }}
        strategyId={selectedStrategyId}
        strategyName={selectedStrategyName}
      />

      {/* Trigger Calculation Modal */}
      {triggerModalOpen && selectedTriggerAlert && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '20px' : '40px'
          }}
          onClick={() => setTriggerModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: isMobile ? '20px' : '30px',
              maxWidth: isMobile ? '100%' : '600px',
              width: '100%',
              maxHeight: isMobile ? '90vh' : '80vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1a202c' }}>
                Trigger Calculation Explanation
              </h3>
              <button
                onClick={() => setTriggerModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  lineHeight: '1',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>Coin:</strong> {selectedTriggerAlert.coin}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>Strategy:</strong> {selectedTriggerAlert.strategyName || 'N/A'}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>Alert Type:</strong>{' '}
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: selectedTriggerAlert.targetType === 'TP' ? '#dbeafe' : '#fee2e2',
                  color: selectedTriggerAlert.targetType === 'TP' ? '#1e40af' : '#991b1b'
                }}>
                  {selectedTriggerAlert.targetType === 'TP' ? 'Take Profit' : 'Trailing Stop Loss'}
                </span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#374151' }}>Position Type:</strong> {selectedTriggerAlert.positionType || 'N/A'}
              </div>
            </div>

            {/* Price Information */}
            {selectedTriggerAlert.entryPrice && selectedTriggerAlert.currentPrice && selectedTriggerAlert.targetPrice && (
              <div style={{
                backgroundColor: '#eff6ff',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #bfdbfe'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
                  Price Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Entry Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a202c' }}>
                      ${formatPrice(selectedTriggerAlert.entryPrice)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Current Price</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a202c' }}>
                      ${formatPrice(selectedTriggerAlert.currentPrice)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Target Price</div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: selectedTriggerAlert.targetType === 'TP' ? '#1e40af' : '#991b1b'
                    }}>
                      ${formatPrice(selectedTriggerAlert.targetPrice)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Profit Calculation */}
            {selectedTriggerAlert.entryPrice && selectedTriggerAlert.currentPrice && (
              <div style={{
                backgroundColor: '#f0fdf4',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #bbf7d0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
                  Profit Calculation
                </h4>
                {(() => {
                  const entryPrice = selectedTriggerAlert.entryPrice!;
                  const currentPrice = selectedTriggerAlert.currentPrice!;
                  const positionType = selectedTriggerAlert.positionType || 'LONG';
                  
                  let profitPercent = 0;
                  let profitFormula = '';
                  
                  if (positionType === 'LONG') {
                    profitPercent = ((currentPrice - entryPrice) / entryPrice) * 100;
                    profitFormula = `((${formatPrice(currentPrice)} - ${formatPrice(entryPrice)}) / ${formatPrice(entryPrice)}) × 100`;
                  } else {
                    profitPercent = ((entryPrice - currentPrice) / entryPrice) * 100;
                    profitFormula = `((${formatPrice(entryPrice)} - ${formatPrice(currentPrice)}) / ${formatPrice(entryPrice)}) × 100`;
                  }
                  
                  const config = selectedTriggerAlert.strategyId ? strategyConfigs.get(selectedTriggerAlert.strategyId) : null;
                  const tpThreshold = config?.takeProfit || null;
                  
                  return (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#374151' }}>Profit from Entry:</strong>
                        </div>
                        <div style={{
                          fontFamily: 'monospace',
                          backgroundColor: 'white',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          fontSize: '13px',
                          color: '#1a202c',
                          marginBottom: '4px'
                        }}>
                          {profitFormula}
                        </div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 700,
                          color: profitPercent >= 0 ? '#059669' : '#dc2626',
                          marginTop: '4px'
                        }}>
                          = {formatPercent(profitPercent)}
                        </div>
                      </div>
                      
                      {tpThreshold !== null && selectedTriggerAlert.targetType === 'TP' && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #bbf7d0' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#374151' }}>Take Profit Threshold:</strong> {tpThreshold}%
                          </div>
                          <div style={{
                            padding: '8px',
                            borderRadius: '4px',
                            backgroundColor: profitPercent >= tpThreshold ? '#d1fae5' : '#fee2e2',
                            color: profitPercent >= tpThreshold ? '#065f46' : '#991b1b',
                            fontWeight: 600,
                            fontSize: '14px'
                          }}>
                            {profitPercent >= tpThreshold 
                              ? `✓ Profit (${formatPercent(profitPercent)}) ≥ TP Threshold (${tpThreshold}%) - TP should trigger`
                              : `✗ Profit (${formatPercent(profitPercent)}) < TP Threshold (${tpThreshold}%) - TP should NOT trigger yet`}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Micro TSL Status */}
            {selectedTriggerAlert.strategyId && (() => {
              const config = strategyConfigs.get(selectedTriggerAlert.strategyId);
              if (!config?.enableMicroTsl) return null;
              
              const activationPercent = config.microTslActivationPercent || 0;
              const distancePercent = config.microTslDistancePercent || 0;
              const entryPrice = selectedTriggerAlert.entryPrice;
              const currentPrice = selectedTriggerAlert.currentPrice;
              
              if (!entryPrice || !currentPrice) return null;
              
              const positionType = selectedTriggerAlert.positionType || 'LONG';
              const profitPercent = positionType === 'LONG'
                ? ((currentPrice - entryPrice) / entryPrice) * 100
                : ((entryPrice - currentPrice) / entryPrice) * 100;
              
              const isMicroTslActive = profitPercent >= activationPercent;
              
              return (
                <div style={{
                  backgroundColor: '#fef3c7',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #fde68a'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
                    🔒 Micro TSL Status
                  </h4>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#374151' }}>Activation:</strong> {activationPercent}%
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#374151' }}>Distance:</strong> {distancePercent}%
                    </div>
                    <div style={{
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: isMicroTslActive ? '#d1fae5' : '#fee2e2',
                      color: isMicroTslActive ? '#065f46' : '#991b1b',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginTop: '8px'
                    }}>
                      {isMicroTslActive 
                        ? `✓ ACTIVE - Profit (${formatPercent(profitPercent)}) ≥ Activation (${activationPercent}%)`
                        : `✗ NOT ACTIVE - Profit (${formatPercent(profitPercent)}) < Activation (${activationPercent}%)`}
                    </div>
                  </div>
                  {isMicroTslActive && (
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid #fde68a',
                      fontSize: '13px',
                      color: '#6b7280',
                      lineHeight: '1.6'
                    }}>
                      <strong>How it works:</strong> Micro TSL tracks the highest price since activation and sets a stop at{' '}
                      <code style={{ backgroundColor: 'white', padding: '2px 4px', borderRadius: '2px' }}>
                        highestPrice × (1 - {distancePercent}%)
                      </code>.
                      It only triggers if the price drops {distancePercent}% from the highest point after activation.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Trigger Condition */}
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
                Trigger Condition
              </h4>
              {(() => {
                if (!selectedTriggerAlert.targetPrice || !selectedTriggerAlert.currentPrice) {
                  return <div style={{ color: '#9ca3af' }}>Insufficient data to calculate trigger status.</div>;
                }

                const isTriggered = selectedTriggerAlert.targetType === 'TP'
                  ? (selectedTriggerAlert.positionType === 'LONG' 
                      ? selectedTriggerAlert.currentPrice >= selectedTriggerAlert.targetPrice 
                      : selectedTriggerAlert.currentPrice <= selectedTriggerAlert.targetPrice)
                  : (selectedTriggerAlert.positionType === 'LONG'
                      ? selectedTriggerAlert.currentPrice <= selectedTriggerAlert.targetPrice
                      : selectedTriggerAlert.currentPrice >= selectedTriggerAlert.targetPrice);

                const currentPrice = formatPrice(selectedTriggerAlert.currentPrice);
                const targetPrice = formatPrice(selectedTriggerAlert.targetPrice);
                const positionType = selectedTriggerAlert.positionType || 'LONG';
                const alertType = selectedTriggerAlert.targetType || 'TP';

                let condition = '';
                let calculation = '';
                let explanation = '';

                if (alertType === 'TP') {
                  if (positionType === 'LONG') {
                    condition = 'Current Price ≥ Target Price';
                    calculation = `${currentPrice} ≥ ${targetPrice}`;
                    explanation = `For LONG positions, Take Profit triggers when the current price reaches or exceeds the target price.`;
                  } else {
                    condition = 'Current Price ≤ Target Price';
                    calculation = `${currentPrice} ≤ ${targetPrice}`;
                    explanation = `For SHORT positions, Take Profit triggers when the current price drops to or below the target price.`;
                  }
                } else {
                  if (positionType === 'LONG') {
                    condition = 'Current Price ≤ Target Price';
                    calculation = `${currentPrice} ≤ ${targetPrice}`;
                    explanation = `For LONG positions, Trailing Stop Loss triggers when the current price drops to or below the trailing stop loss target price.`;
                  } else {
                    condition = 'Current Price ≥ Target Price';
                    calculation = `${currentPrice} ≥ ${targetPrice}`;
                    explanation = `For SHORT positions, Trailing Stop Loss triggers when the current price rises to or above the trailing stop loss target price.`;
                  }
                }

                return (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ color: '#374151' }}>Condition:</strong>
                      </div>
                      <div style={{
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: '#1a202c'
                      }}>
                        {condition}
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ color: '#374151' }}>Current Evaluation:</strong>
                      </div>
                      <div style={{
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        color: isTriggered ? '#059669' : '#dc2626',
                        fontWeight: 600
                      }}>
                        {calculation}
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ color: '#374151' }}>Result:</strong>
                      </div>
                      <div style={{
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: isTriggered ? '#d1fae5' : '#fee2e2',
                        color: isTriggered ? '#065f46' : '#991b1b',
                        fontWeight: 600,
                        fontSize: '14px'
                      }}>
                        {isTriggered ? '✓ TRIGGERED - Condition is met' : '✗ NOT TRIGGERED - Condition is not met'}
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                        {explanation}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Order of Checks */}
            <div style={{
              backgroundColor: '#f3f4f6',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #d1d5db'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
                Order of Checks in Backend
              </h4>
              <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.8' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>1. Micro TSL Check (First Priority)</strong>
                  <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                    - Checked first if Micro TSL is enabled and active
                    <br />
                    - Triggers if price drops {(() => {
                      const config = selectedTriggerAlert.strategyId ? strategyConfigs.get(selectedTriggerAlert.strategyId) : null;
                      return config?.microTslDistancePercent || 'X';
                    })()}% from highest price since activation
                  </div>
                </div>
                <div>
                  <strong>2. Take Profit Check (Second Priority)</strong>
                  <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                    - Checked after Micro TSL
                    <br />
                    - Triggers when profit reaches or exceeds the TP threshold
                  </div>
                </div>
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #d1d5db', fontStyle: 'italic' }}>
                  <strong>Note:</strong> If Micro TSL triggers first, the position closes before TP is checked. 
                  If TP threshold is reached, the position closes before checking if price has dropped from the high.
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#fef3c7',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#92400e'
            }}>
              <strong>Note:</strong> The trigger status is calculated in real-time based on the current market price. 
              The position will be closed automatically when the trigger condition is met.
            </div>
          </div>
        </div>
      )}

      {/* Strategy Logs Modal */}
      <StrategyLogsModal
        isOpen={logsModalOpen}
        onClose={() => {
          setLogsModalOpen(false);
          setLogsStrategyName('');
        }}
        strategyName={logsStrategyName}
      />
    </div>
  );
};

export default LiveDataPanel;
